/* Native dataset viewer/editor.
 *
 * This replaces the Streamlit iframe used by the old data server.  The API is
 * same-origin in production and falls back to the configured inference origin
 * during local development, so the browser never needs to load a second UI.
 */
import { DataGrid } from './data-grid.js';
import { fetchGraphEndpoint } from './utils/graph-request.js';

const API_PATH = '/api/dataset-editor';
const DATABASE_API_PATH = '/api/query-data-source';
const PAGE_SIZE = 500;

const state = {
  mode: 'file',
  filename: null,
  query: '',
  edits: [],
  offset: 0,
  target: null,
  loading: false,
  database: null,
  cellEdits: [],
  requestSerial: 0
};

const gridsByTarget = new WeakMap();

function getSecret() {
  try {
    return (window.userData && window.userData.secret) || window.localStorage.getItem('secret') || '';
  } catch {
    return (window.userData && window.userData.secret) || '';
  }
}

function asText(value, fallback = '') {
  return value === null || value === undefined ? fallback : String(value);
}

function escapeHtml(value) {
  return asText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function showMessage(target, message, className) {
  if (!target) return;
  target.innerHTML = `<div class="dataset-editor-message ${className || ''}">${escapeHtml(message)}</div>`;
}

async function requestJson(path, options) {
  const response = await fetchGraphEndpoint(path, options);
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(payload.error || payload.detail || `Request failed (${response.status})`);
  }
  return payload;
}

function currentRequest(offset = state.offset) {
  return {
    secret: getSecret(),
    filename: state.filename,
    query: state.query,
    edits: state.edits.slice(),
    cell_edits: state.cellEdits.map(edit => ({
      row: edit.row,
      column: edit.column,
      value: edit.value
    })),
    offset,
    limit: PAGE_SIZE
  };
}

function createRequestOptions(method, body) {
  const options = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' }
  };
  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  return options;
}

function destroyGrid(target = null) {
  if (!target) return;
  const grid = gridsByTarget.get(target);
  if (grid && typeof grid.destroy === 'function') {
    try { grid.destroy(); } catch { /* Grid cleanup is best effort. */ }
  }
  gridsByTarget.delete(target);
}

function normalizeRows(payload) {
  const rows = Array.isArray(payload && payload.rows)
    ? payload.rows
    : Array.isArray(payload && payload.data)
      ? payload.data
      : [];
  const columns = Array.isArray(payload && payload.columns) && payload.columns.length
    ? payload.columns.map(value => asText(value))
    : (rows.length && rows[0] && typeof rows[0] === 'object' ? Object.keys(rows[0]) : []);
  return { rows, columns };
}

function renderPayload(target, payload, options = {}) {
  if (!target) return;
  destroyGrid(target);
  target.innerHTML = '';
  target.classList.add('dataset-editor-host');

  const shell = document.createElement('div');
  shell.className = options.compact ? 'dataset-editor-native dataset-editor-native--compact' : 'dataset-editor-native';
  const meta = document.createElement('div');
  meta.className = 'dataset-editor-meta';
  const pager = document.createElement('div');
  pager.className = 'dataset-editor-pager';
  const gridHost = document.createElement('div');
  gridHost.className = 'dataset-editor-grid';
  shell.appendChild(meta);
  shell.appendChild(gridHost);
  shell.appendChild(pager);
  target.appendChild(shell);

  const normalized = normalizeRows(payload);
  const total = Number.isFinite(Number(payload.total_rows))
    ? Number(payload.total_rows)
    : Number.isFinite(Number(payload.row_count)) ? Number(payload.row_count) : normalized.rows.length;
  const offset = Number.isFinite(Number(payload.offset)) ? Number(payload.offset) : 0;
  const rowCount = normalized.rows.length;
  const hasMore = Boolean(payload.has_more) || offset + rowCount < total;
  const first = rowCount ? offset + 1 : 0;
  const last = offset + rowCount;
  meta.innerHTML = `<span>${escapeHtml(`${first}-${last} of ${total} rows`)}</span>`;
  if (Array.isArray(payload.warnings)) {
    payload.warnings.filter(Boolean).forEach(warning => {
      const warningNode = document.createElement('span');
      warningNode.className = 'dataset-editor-warning';
      warningNode.textContent = asText(warning);
      meta.appendChild(warningNode);
    });
  }

  if (!normalized.columns.length && !normalized.rows.length) {
    showMessage(gridHost, 'This dataset has no rows to display.', 'dataset-editor-message--empty');
  } else {
    try {
      const grid = new DataGrid(gridHost, {
        columns: normalized.columns,
        rows: normalized.rows,
        editable: !options.compact && state.mode === 'file',
        onCellEdit: state.mode === 'file' ? rememberCellEdit : null,
        onDownload: !options.compact && state.mode === 'file' ? downloadDataset : null,
        downloadLabel: !options.compact && state.mode === 'file' ? 'Download full CSV' : 'Download'
      });
      gridsByTarget.set(target, grid);
    } catch (error) {
      console.error('Native dataset grid failed to render', error);
      showMessage(gridHost, 'The dataset could not be rendered in the browser.', 'dataset-editor-message--error');
    }
  }

  if (!options.compact) {
    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'dataset-editor-page-button';
    previous.textContent = 'Previous';
    previous.disabled = offset <= 0 || state.loading;
    previous.addEventListener('click', () => loadPage(Math.max(0, offset - PAGE_SIZE)));
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'dataset-editor-page-button';
    next.textContent = 'Next';
    next.disabled = !hasMore || state.loading;
    next.addEventListener('click', () => loadPage(offset + PAGE_SIZE));
    pager.appendChild(previous);
    pager.appendChild(next);
  }
}

function renderLoading(target) {
  if (!target) return;
  destroyGrid(target);
  showMessage(target, 'Loading dataset…', 'dataset-editor-message--loading');
}

function renderError(target, error) {
  const message = error && error.message ? error.message : 'The dataset could not be loaded.';
  destroyGrid(target);
  showMessage(target, message, 'dataset-editor-message--error');
}

async function loadPage(offset = 0) {
  if (!state.target) return null;
  const secret = getSecret();
  if (!secret) {
    state.loading = false;
    renderError(state.target, new Error('Sign in to open a dataset.'));
    return null;
  }
  const target = state.target;
  const requestSerial = ++state.requestSerial;
  state.loading = true;
  state.offset = Math.max(0, Number(offset) || 0);
  renderLoading(target);
  try {
    const payload = await requestJson(API_PATH, createRequestOptions('POST', currentRequest(state.offset)));
    if (requestSerial !== state.requestSerial || target !== state.target) return null;
    state.loading = false;
    renderPayload(target, payload);
    return payload;
  } catch (error) {
    if (requestSerial !== state.requestSerial || target !== state.target) return null;
    state.loading = false;
    renderError(target, error);
    return null;
  }
}

async function openDataset({ filename, filePath, target } = {}) {
  const dialog = document.getElementById('edit-data-dialog');
  const host = target || document.getElementById('edit-data-grid');
  if (!host) return null;
  if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
    try { dialog.showModal(); } catch { dialog.setAttribute('open', ''); }
  }
  state.mode = 'file';
  state.filename = filePath || filename || null;
  state.query = '';
  state.edits = [];
  state.cellEdits = [];
  state.offset = 0;
  state.requestSerial += 1;
  state.target = host;
  state.database = null;
  document.dispatchEvent(new CustomEvent('dataset-editor:opened', {
    detail: { filename: state.filename, label: filename || null }
  }));
  return loadPage(0);
}

function quoteSqlTableName(value) {
  const raw = asText(value).trim();
  if (!raw || raw.includes('\x00') || raw.length > 512) return null;
  const parts = raw.split('.');
  if (parts.length > 2 || parts.some(part => !part.trim())) return null;
  return parts.map(part => `"${part.replace(/"/g, '""')}"`).join('.');
}

function databaseRequest(dataSource, tableName, query) {
  return {
    secret: getSecret(),
    data_source_id: dataSource && dataSource.id,
    query,
    tables: [tableName]
  };
}

async function runDatabaseQuery(dataSource, tableName, query) {
  if (!state.target) return null;
  const safeTable = quoteSqlTableName(tableName);
  if (!safeTable || !dataSource || !dataSource.id) {
    renderError(state.target, new Error('This data source table name is not valid.'));
    return null;
  }
  const target = state.target;
  const requestSerial = ++state.requestSerial;
  const queryText = asText(query).trim() || `SELECT * FROM ${safeTable} LIMIT ${PAGE_SIZE}`;
  state.loading = true;
  renderLoading(target);
  try {
    const payload = await requestJson(
      DATABASE_API_PATH,
      createRequestOptions('POST', databaseRequest(dataSource, tableName, queryText))
    );
    if (requestSerial !== state.requestSerial || target !== state.target) return null;
    state.loading = false;
    const rows = Array.isArray(payload.data) ? payload.data : [];
    renderPayload(target, {
      ...payload,
      rows,
      columns: payload.columns || (rows[0] ? Object.keys(rows[0]) : []),
      total_rows: Number.isFinite(Number(payload.row_count)) ? Number(payload.row_count) : rows.length,
      offset: 0,
      has_more: false
    });
    return payload;
  } catch (error) {
    if (requestSerial !== state.requestSerial || target !== state.target) return null;
    state.loading = false;
    renderError(target, error);
    return null;
  }
}

async function openDatabaseTable(dataSource, tableName, target) {
  const dialog = document.getElementById('edit-data-dialog');
  const host = target || document.getElementById('edit-data-grid');
  if (!host) return null;
  if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
    try { dialog.showModal(); } catch { dialog.setAttribute('open', ''); }
  }
  state.mode = 'database';
  state.target = host;
  state.database = { dataSource, tableName };
  state.filename = null;
  state.query = '';
  state.edits = [];
  state.cellEdits = [];
  state.requestSerial += 1;
  document.dispatchEvent(new CustomEvent('dataset-editor:opened', { detail: { filename: null } }));
  return runDatabaseQuery(dataSource, tableName, '');
}

function rememberCellEdit({ rowIndex, sourceRowIndex, column, value, previousValue }) {
  const absoluteRow = Number.isInteger(sourceRowIndex)
    ? sourceRowIndex
    : state.offset + Math.max(0, Number(rowIndex) || 0);
  const existing = state.cellEdits.find(edit => edit.row === absoluteRow && edit.column === column);
  if (existing) {
    if (Object.is(existing.originalValue, value)) {
      state.cellEdits = state.cellEdits.filter(edit => edit !== existing);
    } else {
      existing.value = value;
    }
  } else if (!Object.is(previousValue, value)) {
    state.cellEdits.push({ row: absoluteRow, column, value, originalValue: previousValue });
  }
}

function safeDownloadName(reference) {
  const name = asText(reference, 'data').split(/[\\/]/).pop() || 'data';
  const withoutPq = name.endsWith('.pq') ? name.slice(0, -3) : name;
  const safe = withoutPq.replace(/[^a-z0-9._-]+/gi, '_').replace(/^[-_.]+|[-_.]+$/g, '') || 'data';
  return safe.toLowerCase().endsWith('.csv') ? safe : `${safe}.csv`;
}

async function downloadDataset() {
  if (state.mode !== 'file' || !state.filename) return false;
  const response = await fetchGraphEndpoint(
    `${API_PATH}/export`,
    createRequestOptions('POST', currentRequest(0))
  );
  if (!response.ok) {
    let payload = {};
    try { payload = await response.json(); } catch { /* Empty error bodies are valid. */ }
    throw new Error(payload.error || payload.detail || `Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const headerName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const anchor = document.createElement('a');
  const url = URL.createObjectURL(blob);
  anchor.href = url;
  anchor.download = safeDownloadName(headerName || state.filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

async function submitEdit() {
  const input = document.getElementById('editdataquery');
  const value = input ? asText(input.value).trim() : '';
  if (!value || state.loading) return false;
  if (typeof window.checkDatasetEditMessageLimit === 'function' && !window.checkDatasetEditMessageLimit()) {
    return false;
  }
  if (state.mode === 'database' && state.database) {
    await runDatabaseQuery(state.database.dataSource, state.database.tableName, value);
    return false;
  }
  if (!state.query) state.query = value;
  else state.edits.push(value);
  state.offset = 0;
  await loadPage(0);
  return false;
}

function bindForm() {
  const form = document.getElementById('editdataaskfelixform');
  if (!form || form.dataset.nativeDatasetEditorBound === 'true') return;
  form.dataset.nativeDatasetEditorBound = 'true';
  form.addEventListener('submit', event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    submitEdit();
  });
}

async function renderTableInto(target, options = {}) {
  if (!target) return null;
  const payload = options.payload || options;
  if (payload && payload.embed) {
    try {
      const url = new URL(payload.embed, window.location.origin);
      const params = url.searchParams;
      const editsText = params.get('edits') || '';
      const edits = editsText ? editsText.split('>-<').filter(Boolean) : [];
      const request = {
        secret: getSecret(),
        filename: params.get('filename') || null,
        query: params.get('query') || '',
        edits,
        offset: 0,
        limit: PAGE_SIZE
      };
      showMessage(target, 'Loading table…', 'dataset-editor-message--loading');
      const result = await requestJson(API_PATH, createRequestOptions('POST', request));
      renderPayload(target, result, { compact: true });
      return result;
    } catch (error) {
      renderError(target, error);
      return null;
    }
  }
  renderPayload(target, payload || {}, { compact: true });
  return payload;
}

document.addEventListener('DOMContentLoaded', bindForm);
if (document.readyState !== 'loading') bindForm();

if (typeof window !== 'undefined') {
  // Make the grid available to the legacy table renderer when the normal
  // bundle is not present.
  window.__DataGrid = window.__DataGrid || DataGrid;
  window.NativeDatasetEditor = {
    openDataset,
    openDatabaseTable,
    renderTableInto,
    runDatabaseQuery,
    submitEdit,
    downloadDataset
  };
}

export { openDataset, openDatabaseTable, renderTableInto, runDatabaseQuery, downloadDataset };
